import { useState, useEffect, useRef, useCallback } from 'react';
import Preloader from '@/components/Screens/Preloader';
import MainScreen, { type GameConnectionStatus } from '@/components/Screens/MainScreen';
import { AudioProvider } from '@/contexts/AudioContext';
import { bgDesktopRoom } from '@/assets/backgrounds';
import { useSessionStore } from '@/store/sessionStore';
import { useGameStore } from '@/store/gameStore';

// Дизайнерские размеры (макет Figma)
const DESIGN_WIDTH = 390;
const DESIGN_HEIGHT = 812;
const SESSION_LOAD_TIMEOUT_MS = 12000;

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [desktopScale, setDesktopScale] = useState(1);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionRetryKey, setConnectionRetryKey] = useState(0);
  const isMobilePortrait = useRef(false);
  
  const { initialize: initSession, isConnected, isDemoMode, sessionId, balanceRaw } = useSessionStore();
  const { setBalance, wsSessionId } = useGameStore();
  
  // Initialize Winnery session on app start
  useEffect(() => {
    initSession();
  }, [initSession]);
  
  // Sync balance from session store to game store (demo mode)
  useEffect(() => {
    if (isConnected && isDemoMode) {
      // In demo mode, game store manages balance locally
      // Initial balance is set from session store
      setBalance(balanceRaw);
    }
  }, [isConnected, isDemoMode, balanceRaw, setBalance]);
  
  // Пытаемся заблокировать ориентацию через Screen Orientation API
  useEffect(() => {
    const lockOrientation = async () => {
      try {
        const orientation = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
        if (orientation?.lock) {
          await orientation.lock('portrait');
        }
      } catch {
        // Не все браузеры поддерживают — тихо пропускаем
      }
    };
    lockOrientation();
  }, []);
  
  // Проверяем ориентацию экрана, тип устройства и вычисляем масштаб
  useEffect(() => {
    const checkDevice = (event?: Event) => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Мобильное устройство = тачскрин + маленький экран
      const isMobile = (
        'ontouchstart' in window || navigator.maxTouchPoints > 0
      ) && Math.min(width, height) < 768;

      // Opening the soft keyboard must not be treated as rotating the phone.
      const editingAmount = document.activeElement instanceof HTMLInputElement;
      if (isMobile && editingAmount && event?.type === 'resize') return;
      
      setIsDesktop(!isMobile);
      
      // Мобилка в landscape
      setIsMobileLandscape(isMobile && width > height);
      
      // Мобилка в портрете
      isMobilePortrait.current = isMobile && width <= height;
      
      if (!isMobile) {
        // Десктоп: масштаб чтобы влезть в окно (по ширине и высоте), но не больше 1
        const scaleW = width / DESIGN_WIDTH;
        const scaleH = height / DESIGN_HEIGHT;
        setDesktopScale(Math.min(scaleW, scaleH, 1));
      }
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);
    
    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);
  
  // Адаптивное масштабирование для мобилки через viewport meta tag
  // Браузер нативно масштабирует контент — без transform: scale() и без чёрных полос
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;
    
    if (isMobilePortrait.current && !isDesktop) {
      // Мобилка в портрете: устанавливаем фиксированную ширину = дизайн
      // Браузер автоматически масштабирует под физический экран
      viewport.setAttribute('content', 
        `width=${DESIGN_WIDTH}, user-scalable=no`
      );
    } else {
      // Десктоп / landscape: стандартный viewport
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
  }, [isDesktop, isMobileLandscape]);
  
  // Keep preloader until the real WS session state is loaded. MainScreen stays
  // mounted behind it so the websocket can connect while the loader is visible.
  useEffect(() => {
    if (!isConnected) return;
    if (isDemoMode || !sessionId || wsSessionId) {
      setIsLoading(false);
    }
  }, [isConnected, isDemoMode, sessionId, wsSessionId]);

  useEffect(() => {
    if (!isLoading || !isConnected || isDemoMode || !sessionId || connectionError) return;
    const timer = setTimeout(() => setConnectionError('SESSION LOAD TIMEOUT'), SESSION_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoading, isConnected, isDemoMode, sessionId, connectionError, connectionRetryKey]);

  const handleConnectionStatusChange = useCallback((status: GameConnectionStatus, message?: string) => {
    if (status === 'error') {
      setConnectionError(message || 'CONNECTION FAILED');
    } else {
      setConnectionError(null);
    }
  }, []);

  const handleConnectionRetry = useCallback(() => {
    setConnectionError(null);
    setConnectionRetryKey(key => key + 1);
  }, []);
  
  // Контент приложения
  const appContent = (
    <AudioProvider>
      <MainScreen
        connectionRetryKey={connectionRetryKey}
        onConnectionStatusChange={handleConnectionStatusChange}
      />
      {isLoading && (
        <div className="absolute inset-0 z-50">
          <Preloader error={connectionError} onRetry={handleConnectionRetry} />
        </div>
      )}
    </AudioProvider>
  );
  
  // На десктопе — показываем фон комнаты + приложение по центру
  // При маленьком окне — масштабируем вниз чтобы всё влезло
  if (isDesktop) {
    const needsScale = desktopScale < 1;
    return (
      <div 
        className="w-screen h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${bgDesktopRoom})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div 
          className="relative overflow-hidden shadow-2xl"
          style={needsScale ? {
            // Маленькое окно — рендерим в дизайнерском размере и масштабируем
            width: `${DESIGN_WIDTH}px`,
            height: `${DESIGN_HEIGHT}px`,
            transform: `scale(${desktopScale})`,
            transformOrigin: 'center center',
          } : {
            // Нормальное/большое окно — фиксированная ширина, полная высота
            width: `${DESIGN_WIDTH}px`,
            height: '100vh',
            maxHeight: '100vh',
          }}
        >
          {appContent}
        </div>
      </div>
    );
  }
  
  // На мобиле в landscape — принудительно поворачиваем контент обратно в портрет
  if (isMobileLandscape) {
    return (
      <div
        style={{
          position: 'fixed',
          top: '100%',
          left: 0,
          width: '100vh',   // Короткая сторона (высота viewport в landscape)
          height: '100vw',  // Длинная сторона (ширина viewport в landscape)
          transform: 'rotate(-90deg)',
          transformOrigin: 'top left',
          overflow: 'hidden',
        }}
      >
        {appContent}
      </div>
    );
  }
  
  // На мобиле в портрете — масштабирование через viewport meta tag (выше)
  // Браузер нативно рендерит контент в 390px ширину и масштабирует под экран
  // Никаких обёрток, никаких transform — контент заполняет весь экран без чёрных полос
  return appContent;
}

export default App;

